import React, { ReactElement } from 'react'
import { IntlProvider } from 'react-intl'
import { useParams, Navigate } from 'react-router'

import { Language, LanguageContext } from './LanguageContext'
import enMessages from './locales/en.json'
import frMessages from './locales/fr.json'

export const DEFAULT_LANGUAGE = 'fr'

const messages: Record<string, Record<string, string>> = {
  en: enMessages,
  fr: frMessages,
}

type Props = {
  children: ReactElement
}

const LanguageWrapper = ({ children }: Props) => {
  const { lang } = useParams()
  const defaultLang = localStorage.getItem('preferredLang') || DEFAULT_LANGUAGE

  const supportedLanguages = React.useMemo(() => ['en', 'fr'], [])

  React.useEffect(() => {
    if (lang && supportedLanguages.includes(lang)) {
      localStorage.setItem('preferredLang', lang)
    }
  }, [lang, supportedLanguages])

  const currentLang = React.useMemo(() => {
    return lang && supportedLanguages.includes(lang) ? lang : defaultLang
  }, [lang, defaultLang, supportedLanguages])

  if (!lang || !supportedLanguages.includes(lang)) {
    return <Navigate to={`/${defaultLang}`} replace />
  }

  return (
    <LanguageContext.Provider value={currentLang as Language}>
      <IntlProvider locale={currentLang} messages={messages[currentLang] || {}}>
        {children}
      </IntlProvider>
    </LanguageContext.Provider>
  )
}

export default LanguageWrapper
